<template>
  <div class="article-edit">
    <header class="edit-header">
      <div class="edit-heading">
        <p class="edit-eyebrow">Markdown 工作台</p>
        <h1>{{ isEdit ? '编辑文章' : '发布文章' }}</h1>
        <p class="save-status" role="status" aria-live="polite">
          <span class="status-dot" :class="saveStatusClass" aria-hidden="true"></span>
          {{ saveStatusText }}
          <span class="shortcut-hint">Ctrl/⌘ + S 保存</span>
        </p>
      </div>

      <div class="header-actions">
        <el-button :disabled="saving" @click="handleCancel">取消</el-button>
        <el-button
          type="primary"
          :loading="saving"
          :disabled="loadingArticle || Boolean(loadError)"
          @click="handleSubmit">
          {{ saving ? '正在保存' : (isEdit ? '保存修改' : '发布文章') }}
        </el-button>
      </div>
    </header>

    <section v-if="loadError" class="load-error" role="alert">
      <h2>文章内容加载失败</h2>
      <p>{{ loadError }}</p>
      <el-button type="primary" @click="initialize">重新加载</el-button>
    </section>

    <el-form
      v-else
      ref="articleForm"
      :model="articleForm"
      :rules="articleRules"
      label-position="top"
      class="edit-form">
      <section class="metadata-card" aria-labelledby="metadata-title">
        <div class="section-heading">
          <div>
            <h2 id="metadata-title">文章信息</h2>
            <p>这些内容会显示在文章标题区和列表摘要中。</p>
          </div>
        </div>

        <div class="metadata-grid">
          <el-form-item label="标题" prop="title" class="title-field">
            <el-input
              v-model="articleForm.title"
              maxlength="120"
              show-word-limit
              placeholder="输入清晰、具体的文章标题"
              class="title-input">
            </el-input>
          </el-form-item>

          <el-form-item label="分类" prop="cid" class="category-field">
            <el-select
              v-model="articleForm.cid"
              filterable
              placeholder="选择文章分类"
              class="category-select">
              <el-option
                v-for="category in categories"
                :key="category.id"
                :label="category.name"
                :value="category.id">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="描述" prop="desc" class="description-field">
            <el-input
              v-model="articleForm.desc"
              type="textarea"
              :rows="3"
              maxlength="200"
              show-word-limit
              resize="vertical"
              placeholder="用一两句话概括文章内容，帮助读者快速判断是否需要阅读。">
            </el-input>
          </el-form-item>
        </div>
      </section>

      <section class="editor-card" aria-labelledby="content-title">
        <div class="section-heading editor-heading">
          <div>
            <h2 id="content-title">正文</h2>
            <p>
              支持 GFM、任务列表、脚注、<code>$行内公式$</code>、
              <code>$$块级公式$$</code>、<code>```mermaid</code> 与
              <code>```plantuml</code> 图表；发布时原始 HTML 会按文本处理。
            </p>
          </div>
          <span class="editor-mode">默认分屏模式</span>
        </div>

        <el-form-item prop="content" class="content-field">
          <LoadingSpinner v-if="loadingArticle" text="正在准备编辑器..." />
          <MarkdownEditor
            v-else
            ref="markdownEditor"
            v-model="articleForm.content"
            :disabled="saving"
            :min-height="620"
            @ready="editorReady = true"
            @save="handleSubmit" />
        </el-form-item>
      </section>
    </el-form>
  </div>
</template>

<script>
import { getArticleInfo, editArticle, createArticle, getCategories } from '@/api'
import MarkdownEditor from '@/components/MarkdownEditor'
import LoadingSpinner from '@/components/LoadingSpinner'

const DRAFT_PREFIX = 'xjmginblog:article-draft:'
const AUTOSAVE_DELAY = 1200

function emptyArticleForm() {
  return {
    title: '',
    cid: '',
    desc: '',
    content: ''
  }
}

export default {
  name: 'ArticleEdit',
  components: {
    MarkdownEditor,
    LoadingSpinner
  },
  data() {
    const validateDesc = (rule, value, callback) => {
      if (!String(value || '').trim()) {
        callback(new Error('请输入文章描述'))
        return
      }
      if (value.length > 200) {
        callback(new Error('描述长度不能超过 200 个字符'))
        return
      }
      callback()
    }

    return {
      isEdit: false,
      articleForm: emptyArticleForm(),
      articleRules: {
        title: [
          { required: true, message: '请输入文章标题', trigger: 'blur' },
          { max: 120, message: '标题长度不能超过 120 个字符', trigger: 'blur' }
        ],
        cid: [{ required: true, message: '请选择分类', trigger: 'change' }],
        desc: [{ validator: validateDesc, trigger: 'blur' }],
        content: [{ required: true, message: '请输入文章内容', trigger: 'change' }]
      },
      categories: [],
      loadingArticle: true,
      loadError: '',
      saving: false,
      editorReady: false,
      hydrated: false,
      dirty: false,
      allowLeave: false,
      draftSavedAt: null,
      persistedSnapshot: '',
      autosaveTimer: null
    }
  },
  computed: {
    articleId() {
      return this.$route.params.id || this.$route.query.id || ''
    },
    draftKey() {
      return `${DRAFT_PREFIX}${this.articleId || 'new'}`
    },
    saveStatusText() {
      if (this.saving) return '正在保存到服务器…'
      if (this.dirty && this.draftSavedAt) {
        return `草稿已自动保存于 ${this.formatTime(this.draftSavedAt)}`
      }
      if (this.dirty) return '有尚未写入草稿的更改'
      return this.isEdit ? '当前内容已与服务器同步' : '开始输入后会自动保存本地草稿'
    },
    saveStatusClass() {
      if (this.saving) return 'is-saving'
      if (this.dirty) return 'is-draft'
      return 'is-saved'
    }
  },
  watch: {
    articleForm: {
      deep: true,
      handler() {
        if (!this.hydrated) return
        this.dirty = this.serializeForm() !== this.persistedSnapshot
        if (this.dirty) this.scheduleDraftSave()
      }
    }
  },
  created() {
    this.initialize()
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    clearTimeout(this.autosaveTimer)
  },
  beforeRouteLeave(to, from, next) {
    if (this.allowLeave || !this.dirty) {
      next()
      return
    }

    this.flushDraft()
    this.$confirm('当前修改已保存为本地草稿，仍要离开编辑器吗？', '离开编辑器', {
      confirmButtonText: '保留草稿并离开',
      cancelButtonText: '继续编辑',
      type: 'warning'
    }).then(() => {
      this.allowLeave = true
      next()
    }).catch(() => next(false))
  },
  methods: {
    async initialize() {
      this.loadingArticle = true
      this.loadError = ''
      this.hydrated = false
      this.isEdit = Boolean(this.articleId)
      this.articleForm = emptyArticleForm()

      try {
        const tasks = [this.fetchCategories()]
        if (this.isEdit) tasks.push(this.fetchArticleInfo(this.articleId))
        await Promise.all(tasks)

        this.persistedSnapshot = this.serializeForm()
        this.dirty = false
        this.hydrated = true
        await this.$nextTick()
        await this.restoreDraft()
      } catch (error) {
        console.error('初始化文章编辑器失败:', error)
        this.loadError = error.message || '请稍后重试'
      } finally {
        this.loadingArticle = false
      }
    },
    async fetchCategories() {
      try {
        const res = await getCategories()
        this.categories = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        console.error('获取分类列表错误:', error)
        this.categories = []
        this.$message.error('获取分类列表失败')
      }
    },
    async fetchArticleInfo(id) {
      const res = await getArticleInfo(id)
      if (!res || Number(res.status) !== 200 || !res.data) {
        throw new Error((res && res.message) || '获取文章详情失败')
      }

      const article = res.data
      this.articleForm = {
        id: article.ID,
        title: article.title || '',
        cid: article.cid || '',
        desc: article.desc || '',
        content: article.content || ''
      }
    },
    serializeForm(form = this.articleForm) {
      return JSON.stringify({
        title: String(form.title || ''),
        cid: form.cid || '',
        desc: String(form.desc || ''),
        content: String(form.content || '')
      })
    },
    scheduleDraftSave() {
      clearTimeout(this.autosaveTimer)
      this.autosaveTimer = setTimeout(this.flushDraft, AUTOSAVE_DELAY)
    },
    flushDraft() {
      clearTimeout(this.autosaveTimer)
      if (!this.hydrated || !this.dirty) return

      try {
        const savedAt = Date.now()
        localStorage.setItem(this.draftKey, JSON.stringify({
          savedAt,
          form: {
            title: this.articleForm.title,
            cid: this.articleForm.cid,
            desc: this.articleForm.desc,
            content: this.articleForm.content
          }
        }))
        this.draftSavedAt = savedAt
      } catch (error) {
        console.error('保存本地草稿失败:', error)
        this.$message.warning('本地草稿保存失败，请尽快发布或手动备份内容')
      }
    },
    async restoreDraft() {
      const rawDraft = localStorage.getItem(this.draftKey)
      if (!rawDraft) return

      try {
        const draft = JSON.parse(rawDraft)
        if (!draft.form || this.serializeForm(draft.form) === this.persistedSnapshot) {
          localStorage.removeItem(this.draftKey)
          return
        }

        const savedAt = draft.savedAt ? this.formatTime(draft.savedAt) : '此前'
        await this.$confirm(`检测到 ${savedAt} 保存的本地草稿，是否恢复？`, '恢复草稿', {
          confirmButtonText: '恢复草稿',
          cancelButtonText: '使用服务器版本',
          type: 'info'
        })

        this.articleForm = {
          ...emptyArticleForm(),
          ...draft.form,
          ...(this.isEdit ? { id: this.articleForm.id } : {})
        }
        this.draftSavedAt = draft.savedAt || Date.now()
        this.dirty = true
        this.$message.success('已恢复本地草稿')
      } catch (error) {
        if (error === 'cancel' || error === 'close') {
          localStorage.removeItem(this.draftKey)
          this.draftSavedAt = null
        } else {
          console.warn('读取本地草稿失败:', error)
          localStorage.removeItem(this.draftKey)
        }
      }
    },
    clearDraft() {
      clearTimeout(this.autosaveTimer)
      localStorage.removeItem(this.draftKey)
      this.draftSavedAt = null
    },
    formatTime(timestamp) {
      const date = new Date(timestamp)
      if (Number.isNaN(date.getTime())) return '刚刚'
      return date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    handleBeforeUnload(event) {
      if (!this.dirty) return
      this.flushDraft()
      event.preventDefault()
      event.returnValue = ''
    },
    handleCancel() {
      this.$router.push('/')
    },
    validateForm() {
      return new Promise(resolve => {
        this.$refs.articleForm.validate(valid => resolve(valid))
      })
    },
    async handleSubmit() {
      if (this.saving || this.loadingArticle || this.loadError) return

      const valid = await this.validateForm()
      if (!valid) {
        this.$message.warning('请先补全文章信息和正文')
        return
      }

      this.saving = true
      try {
        const res = this.isEdit
          ? await editArticle(this.articleForm.id, this.articleForm)
          : await createArticle(this.articleForm)

        if (!res || Number(res.status) !== 200) {
          throw new Error((res && res.message) || '保存文章失败')
        }

        const savedId = this.isEdit
          ? this.articleForm.id
          : (res.data && (res.data.ID || res.data.id))

        this.persistedSnapshot = this.serializeForm()
        this.dirty = false
        this.allowLeave = true
        this.clearDraft()
        this.$message.success(this.isEdit ? '文章已更新' : '文章已发布')

        if (savedId) {
          await this.$router.push(`/article/detail/${savedId}`)
        } else {
          await this.$router.push('/')
        }
      } catch (error) {
        console.error('保存文章错误:', error)
        this.$message.error(error.message || (this.isEdit ? '编辑失败' : '发布失败'))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.article-edit {
  width: min(calc(100% - 3rem), 1480px);
  margin: 0 auto;
  padding: 1rem 0 4rem;
}

.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
  margin-bottom: 1.5rem;
}

.edit-heading {
  min-width: 0;
}

.edit-eyebrow {
  margin: 0 0 0.35rem;
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.edit-header h1 {
  margin: 0;
  font-size: clamp(2rem, 4vw, 2.75rem);
  line-height: 1.1;
  color: var(--text-color);
}

.save-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 1.5rem;
  margin: 0.75rem 0 0;
  color: var(--text-secondary);
  font-size: 0.88rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: #78909c;
}

.status-dot.is-draft {
  background: #d97706;
}

.status-dot.is-saving {
  background: var(--primary-color);
  animation: status-pulse 1s ease-in-out infinite;
}

.status-dot.is-saved {
  background: #2f855a;
}

.shortcut-hint {
  margin-left: 0.35rem;
  padding-left: 0.75rem;
  border-left: 1px solid rgba(107, 95, 76, 0.2);
  color: #718096;
}

.header-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 0.75rem;
  padding-bottom: 0.15rem;
}

.header-actions :deep(.el-button) {
  min-width: 104px;
  min-height: 42px;
}

.edit-form {
  display: grid;
  gap: 1.25rem;
}

.metadata-card,
.editor-card,
.load-error {
  background: var(--card-bg);
  border: 1px solid rgba(107, 95, 76, 0.1);
  border-radius: 14px;
  box-shadow: 0 4px 18px rgba(107, 95, 76, 0.06);
}

.metadata-card {
  padding: 1.5rem;
}

.editor-card {
  padding: 1.25rem;
}

.load-error {
  padding: 3rem;
  text-align: center;
}

.load-error h2 {
  margin-top: 0;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.section-heading h2 {
  margin: 0 0 0.35rem;
  font-size: 1.15rem;
  line-height: 1.3;
}

.section-heading p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
}

.section-heading code {
  padding: 0.1em 0.35em;
  border-radius: 4px;
  background: rgba(107, 95, 76, 0.09);
  color: #4a4237;
  font-size: 0.9em;
}

.editor-mode {
  flex: 0 0 auto;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: rgba(67, 97, 238, 0.08);
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 600;
}

.metadata-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(240px, 1fr);
  column-gap: 1.25rem;
}

.description-field {
  grid-column: 1 / -1;
}

.edit-form :deep(.el-form-item) {
  margin-bottom: 1.25rem;
}

.edit-form :deep(.el-form-item__label) {
  padding-bottom: 0.45rem;
  color: var(--text-color);
  font-weight: 600;
  line-height: 1.3;
}

.edit-form :deep(.el-input__inner),
.edit-form :deep(.el-textarea__inner) {
  border-color: rgba(107, 95, 76, 0.18);
  background-color: #fff;
  color: var(--text-color);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.edit-form :deep(.el-input__inner:hover),
.edit-form :deep(.el-textarea__inner:hover) {
  border-color: rgba(67, 97, 238, 0.42);
}

.edit-form :deep(.el-input__inner:focus),
.edit-form :deep(.el-textarea__inner:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
}

.title-input :deep(.el-input__inner) {
  height: 46px;
  font-size: 1.08rem;
  font-weight: 600;
}

.category-select {
  width: 100%;
}

.content-field {
  margin-bottom: 0 !important;
}

.content-field :deep(.el-form-item__content) {
  line-height: normal;
}

@keyframes status-pulse {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
}

@media (max-width: 900px) {
  .article-edit {
    width: min(calc(100% - 2rem), 1480px);
  }

  .metadata-grid {
    grid-template-columns: 1fr;
  }

  .description-field {
    grid-column: auto;
  }

  .shortcut-hint,
  .editor-mode {
    display: none;
  }
}

@media (max-width: 640px) {
  .edit-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions :deep(.el-button) {
    flex: 1;
  }

  .metadata-card,
  .editor-card {
    padding: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .status-dot.is-saving {
    animation: none;
  }
}
</style>
